// [S-063] Re-attestation schedule — platform-wide L1 surface.
//
// Direct port of docs/Wireframes/ReAttestation.html. Master Rule 1.6:
// L1-only page; no source-mix indicators anywhere. Every color and font
// reference resolves to design-system tokens.
//
// Data: useReAttestationSchedule() reads from
// `GET /platform/re-attestation/schedule`. The endpoint already filters
// to L1 templates with both `anchorCBA` and `reAttestationDueAt` set;
// rows arrive sorted by due date ASC. The component groups by anchorCBA
// for render.
//
// Role gate: <RequireRole minRole="platform_admin"> wraps the route in
// App.tsx. The service also gates server-side — defense in depth.
import { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Dialog, LoadingState, PageHead } from "../components";
import {
  useReAttestationSchedule,
  useScheduleReAttestation,
  type ScheduleRow,
} from "../data/reAttestationQuery";
import { useTenantsList } from "../data/tenantsQuery";
import { useIdentityLabels, type IdentityLabels } from "../data/identitiesQuery";

export function ReAttestation(): JSX.Element {
  const { data, isLoading, isError, refetch, isFetching } =
    useReAttestationSchedule();
  // Real platform tenant count drives the "included / total" inclusion display.
  // Degrades to null (rendered "—") rather than a fabricated number if the list
  // is still loading or errored — never a hardcoded total.
  const tenantsQuery = useTenantsList();
  const totalTenants = tenantsQuery.data?.tenants.length ?? null;
  const [scheduling, setScheduling] = useState<ScheduleRow | null>(null);
  // Called unconditionally (before the loading/error early returns) per the
  // rules of hooks — degrades to an empty uid list pre-load.
  const ownerLabels = useIdentityLabels(
    data?.schedule.map((r) => r.submittedBy) ?? []
  ).data;

  const showLoading = isLoading || data === undefined;
  const rows = data?.schedule ?? [];
  const buckets = computeBuckets(rows);
  const groups = groupByCBA(rows);

  return (
    <>
      <PageHead
        eyebrow="Platform · re-approval schedule"
        title={
          <>
            Keep the chain <em>current.</em>
          </>
        }
        sub={
          showLoading
            ? "Fetching certified templates schedule."
            : isError
              ? "The platform read API responded with an error. Try again in a moment."
              : "Every certified template is anchored to a CBA and shipped to all organizations by default. Re-approve quarterly so the approval trail stays valid platform-wide. Organization admins manage their own inclusion list."
        }
        cta={
          !showLoading && !isError ? (
            <>
              <span className="reatt__head__refresh">
                {`${rows.length} templates · ${buckets.high} due in 14d · ${totalTenants ?? "—"} tenants`}
              </span>
              <Button
                variant="secondary"
                className="btn--s"
                onClick={() => {
                  void refetch();
                }}
                disabled={isFetching}
                data-testid="reatt-refresh"
              >
                {isFetching ? "Refreshing…" : "Refresh"}
              </Button>
            </>
          ) : undefined
        }
      />

      {showLoading && <LoadingState label="Loading schedule…" />}

      {!showLoading && isError && (
        <Alert label="Schedule unavailable" tone="error">
          The platform read API responded with an error. Try again in a
          moment.
        </Alert>
      )}

      {!showLoading && !isError && (
        <>
          {rows.length === 0 ? (
            <EmptyState />
          ) : (
            <>
              <div className="tbl tbl--audit tbl--reatt" data-testid="reatt-table">
                <div className="tbl__row tbl__row--head">
                  <span>Template</span>
                  <span>Title</span>
                  <span>State</span>
                  <span>Anchor CBA</span>
                  <span>Attorney</span>
                  <span>Last approved</span>
                  <span>Organizations</span>
                  <span>Due in</span>
                </div>

                {groups.map(({ cba, rows: groupRows }) => (
                  <ScheduleGroup
                    key={cba}
                    cba={cba}
                    rows={groupRows}
                    totalTenants={totalTenants}
                    ownerLabels={ownerLabels}
                    onSchedule={(row) => setScheduling(row)}
                  />
                ))}
              </div>

              <BucketStrip
                buckets={buckets}
                totalRows={rows.length}
                totalTenants={totalTenants}
                templatesWithExclusions={rows.filter(
                  (r) => r.excludedTenantCount > 0
                ).length}
              />
            </>
          )}
        </>
      )}

      <ScheduleDialog
        row={scheduling}
        onClose={() => setScheduling(null)}
      />
    </>
  );
}

function ScheduleGroup({
  cba,
  rows,
  totalTenants,
  ownerLabels,
  onSchedule,
}: {
  cba: string;
  rows: ScheduleRow[];
  totalTenants: number | null;
  ownerLabels: IdentityLabels | undefined;
  onSchedule: (row: ScheduleRow) => void;
}): JSX.Element {
  return (
    <>
      <div className="tbl__row reatt__grp">
        <b>{cba}</b>
        <span className="ct">
          {rows.length} template{rows.length === 1 ? "" : "s"}
        </span>
      </div>
      {rows.map((row) => (
        <ScheduleRowView
          key={row.id}
          row={row}
          totalTenants={totalTenants}
          ownerLabel={ownerLabels?.get(row.submittedBy)}
          onSchedule={() => onSchedule(row)}
        />
      ))}
    </>
  );
}

function ScheduleRowView({
  row,
  totalTenants,
  ownerLabel,
  onSchedule,
}: {
  row: ScheduleRow;
  totalTenants: number | null;
  ownerLabel: string | undefined;
  onSchedule: () => void;
}): JSX.Element {
  const days = daysUntil(row.reAttestationDueAt);
  const dueBucket = bucketOf(days);
  const stateClass = stateClassFor(row.lifecycleState);
  const included =
    totalTenants === null ? null : totalTenants - row.excludedTenantCount;
  const fullyIncluded = row.excludedTenantCount === 0;
  return (
    <div className="tbl__row" data-testid={`reatt-row-${row.id}`}>
      <div className="reatt__tmpl">
        <span className="src-edge src-edge--cert" aria-hidden="true" />
        <span>
          {capitalize(row.guild)}
          <em> {prettyTier(row.tier)}</em>
        </span>
      </div>
      <div className="reatt__title">{titleFor(row)}</div>
      <span>
        <span className={`reatt__state ${stateClass}`}>
          {row.lifecycleState}
        </span>
      </span>
      <span className="reatt__cba">{row.anchorCBA}</span>
      <span className="reatt__who">{ownerLabel ?? "—"}</span>
      <span className="reatt__when">{formatShortDate(row.effectiveFrom)}</span>
      <span
        className={`reatt__tn ${fullyIncluded ? "reatt__tn--full" : "reatt__tn--excl"}`}
      >
        <b>
          {included ?? "—"} / {totalTenants ?? "—"}
        </b>
        <em>
          {fullyIncluded
            ? "all included"
            : `${row.excludedTenantCount} excluded`}
        </em>
      </span>
      <span className="reatt__rowact">
        <span
          className={`reatt__due reatt__due--${dueBucket}`}
          data-testid={`reatt-due-${row.id}`}
        >
          {days <= 0 ? "today" : `${days} days`}
        </span>
        <Button
          variant="secondary"
          className="btn--s"
          onClick={onSchedule}
          data-testid={`reatt-schedule-${row.id}`}
        >
          Schedule
        </Button>
      </span>
    </div>
  );
}

function BucketStrip({
  buckets,
  totalRows,
  totalTenants,
  templatesWithExclusions,
}: {
  buckets: { high: number; med: number; low: number };
  totalRows: number;
  totalTenants: number | null;
  templatesWithExclusions: number;
}): JSX.Element {
  return (
    <div className="reatt__buckets" data-testid="reatt-buckets">
      <span className="reatt__bucket reatt__bucket--high">
        <span className="dot" />
        <b>{buckets.high}</b> due in 14 days
      </span>
      <span className="reatt__bucket reatt__bucket--med">
        <span className="dot" />
        <b>{buckets.med}</b> due in 30 days
      </span>
      <span className="reatt__bucket reatt__bucket--low">
        <span className="dot" />
        <b>{buckets.low}</b> due in 90 days
      </span>
      <div className="reatt__buckets__cap">
        Templates: <b>{totalRows}</b> templates ·{" "}
        <b>{totalTenants ?? "—"}</b> tenants
        <br />
        <b>{totalRows}</b> in re-attestation window ·{" "}
        <b>{templatesWithExclusions}</b> with tenant exclusions
      </div>
    </div>
  );
}

function EmptyState(): JSX.Element {
  return (
    <div className="reatt__empty" data-testid="reatt-empty">
      <div className="reatt__empty__eye">Nothing due in this window</div>
      <div className="reatt__empty__hint">
        No certified L1 templates have a re-attestation date set yet. Set
        the anchor CBA + due date when a template is certified or backfill
        existing rows via TemplateService.
      </div>
    </div>
  );
}

function ScheduleDialog({
  row,
  onClose,
}: {
  row: ScheduleRow | null;
  onClose: () => void;
}): JSX.Element | null {
  const mutation = useScheduleReAttestation();
  const defaultDate = useMemo(() => {
    if (row === null) return "";
    return defaultScheduledFor(row.reAttestationDueAt);
  }, [row]);
  const [scheduledFor, setScheduledFor] = useState("");
  const [note, setNote] = useState("");

  // Reset the form whenever the modal switches templates.
  useMemoReset(row?.id ?? null, () => {
    setScheduledFor(defaultDate);
    setNote("");
    mutation.reset();
  });

  if (row === null) return null;
  const submitDisabled =
    mutation.isPending || scheduledFor.length === 0;

  return (
    <Dialog
      open={row !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={
        <span className="reatt__modal__hd">
          Schedule re-approval — {titleFor(row)}
        </span>
      }
      description={
        <span className="reatt__modal__sub">
          Sets a re-attestation target date and writes an audit event. No
          template state change.
        </span>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate(
            {
              templateId: row.id,
              scheduledFor: new Date(scheduledFor).toISOString(),
              note: note.trim().length === 0 ? null : note.trim(),
            },
            {
              onSuccess: onClose,
            }
          );
        }}
      >
        <div className="reatt__modal__row">
          <label htmlFor="reatt-date">
            Target date <em>re_attestation_due_at − 14d</em>
          </label>
          <input
            id="reatt-date"
            className="reatt__date"
            type="date"
            value={scheduledFor}
            onChange={(e) => setScheduledFor(e.target.value)}
            required
            data-testid="reatt-modal-date"
          />
        </div>

        <div className="reatt__modal__row">
          <label htmlFor="reatt-note">
            Note <em>optional</em>
          </label>
          <textarea
            id="reatt-note"
            className="reatt__note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add context for the audit record…"
            maxLength={500}
            data-testid="reatt-modal-note"
          />
        </div>

        {mutation.isError && (
          <div
            className="reatt__modal__row"
            role="alert"
            style={{ color: "var(--cs-risk-high-text)" }}
            data-testid="reatt-modal-error"
          >
            Could not schedule re-attestation. Try again, or check your
            connection.
          </div>
        )}

        <div className="reatt__modal__ft">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={mutation.isPending}
            type="button"
            data-testid="reatt-modal-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={submitDisabled}
            data-testid="reatt-modal-submit"
          >
            {mutation.isPending ? "Scheduling…" : "Schedule"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Pure helpers
// ────────────────────────────────────────────────────────────────────────

function computeBuckets(rows: ScheduleRow[]): {
  high: number;
  med: number;
  low: number;
} {
  let high = 0;
  let med = 0;
  let low = 0;
  for (const r of rows) {
    const d = daysUntil(r.reAttestationDueAt);
    if (d < 14) high += 1;
    else if (d < 30) med += 1;
    else if (d < 90) low += 1;
  }
  return { high, med, low };
}

function groupByCBA(
  rows: ScheduleRow[]
): { cba: string; rows: ScheduleRow[] }[] {
  // Server returns rows ordered by reAttestationDueAt ASC. We preserve
  // first-encounter order of the CBA groups so the page reads top-to-
  // bottom in the same sequence as the underlying due-date sort.
  const order: string[] = [];
  const buckets = new Map<string, ScheduleRow[]>();
  for (const r of rows) {
    if (!buckets.has(r.anchorCBA)) {
      order.push(r.anchorCBA);
      buckets.set(r.anchorCBA, []);
    }
    buckets.get(r.anchorCBA)!.push(r);
  }
  return order.map((cba) => ({ cba, rows: buckets.get(cba)! }));
}

function daysUntil(iso: string): number {
  const due = new Date(iso).getTime();
  const now = Date.now();
  return Math.max(0, Math.floor((due - now) / (1000 * 60 * 60 * 24)));
}

function bucketOf(days: number): "high" | "med" | "low" {
  if (days < 14) return "high";
  if (days < 30) return "med";
  return "low";
}

function stateClassFor(s: string): string {
  switch (s) {
    case "certified":
      return "reatt__state--cert";
    case "re_certified":
      return "reatt__state--rec";
    case "under_review":
      return "reatt__state--rev";
    default:
      return "reatt__state--cert";
  }
}

function titleFor(row: ScheduleRow): string {
  // We don't carry a human title from the server today (the entity only
  // has `content`). Render a synthesized title from deal type + guild
  // until the API surfaces a title field — a backfill follow-up.
  const dealTypePretty = row.dealType.replace(/_/g, " ");
  return `${capitalize(row.guild)} ${capitalize(dealTypePretty)}`;
}

function capitalize(s: string): string {
  return s.length === 0 ? s : s[0]!.toUpperCase() + s.slice(1);
}

function prettyTier(t: string): string {
  return capitalize(t.replace(/_/g, " "));
}

function formatShortDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function defaultScheduledFor(reAttestationDueAtIso: string): string {
  // 14 days before due_at, formatted YYYY-MM-DD for a date input.
  const due = new Date(reAttestationDueAtIso);
  const fourteenDaysBefore = new Date(due.getTime() - 14 * 24 * 60 * 60 * 1000);
  // Use toISOString().slice(0, 10) for stable UTC date — the date input
  // value is just a calendar day, not a wall-clock time.
  return fourteenDaysBefore.toISOString().slice(0, 10);
}

// Tiny "reset when the dependency changes" helper for the modal form
// fields. The render-time `run` closure is captured by intent — we only
// want to re-run when the row identity changes, not when the setters
// change.
function useMemoReset(key: string | null, run: () => void): void {
  const runRef = useRef(run);
  runRef.current = run;
  useEffect(() => {
    runRef.current();
  }, [key]);
}
